import React, { useState, useEffect } from 'react';
import { deliveryDocumentImportService, suppliersService } from '../services';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, X, ChevronDown, Loader2, Plus, AlertTriangle } from 'lucide-react';
import { getApiError } from '@/lib/utils';

/**
 * Step 2 header supplier resolution. Suggests the closest EXISTING AZ
 * supplier via simple normalised-name comparison (computed server-side,
 * never persisted on its own) — the reviewer must explicitly Confirm it,
 * pick a different one from the full list, or add one inline with an
 * explicit save and a duplicate-name warning. Nothing here ever silently
 * assigns or creates a supplier.
 */
export function DeliverySupplierResolver({ importId, supplierNameRaw, reviewedSupplier, onChange, isEditable }) {
  const { toast } = useToast();
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [suppliers, setSuppliers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [similar, setSimilar] = useState([]);
  const [checkingName, setCheckingName] = useState(false);

  useEffect(() => {
    if (reviewedSupplier || !importId) return;
    setLoadingSuggestion(true);
    deliveryDocumentImportService.getSupplierSuggestion(importId)
      .then(setSuggestion).catch(() => setSuggestion(null)).finally(() => setLoadingSuggestion(false));
  }, [importId, reviewedSupplier]);

  useEffect(() => {
    if (!addOpen || !newName.trim()) { setSimilar([]); return; }
    const t = setTimeout(() => {
      setCheckingName(true);
      deliveryDocumentImportService.checkSupplierName(newName.trim())
        .then((d) => setSimilar(d.similar_suppliers || [])).catch(() => {}).finally(() => setCheckingName(false));
    }, 400);
    return () => clearTimeout(t);
  }, [newName, addOpen]);

  const save = async (supplierId) => {
    setSaving(true);
    try {
      const updated = await deliveryDocumentImportService.setReviewedSupplier(importId, supplierId);
      onChange(updated.reviewed_supplier, updated.reviewed_supplier_id);
      setPickerOpen(false);
      return true;
    } catch (err) {
      toast({ title: 'Could not save supplier', description: getApiError(err), variant: 'destructive' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openPicker = async (open) => {
    setPickerOpen(open);
    if (open && !suppliers) {
      try {
        const data = await suppliersService.list({ limit: 500, is_active: true });
        setSuppliers(data.items || []);
      } catch (err) {
        toast({ title: 'Could not load suppliers', description: getApiError(err), variant: 'destructive' });
      }
    }
  };

  const createSupplier = async () => {
    setSaving(true);
    try {
      const created = await suppliersService.create({ supplier_name: newName.trim() });
      setSuppliers(null);
      setAddOpen(false);
      const assigned = await save(created.id);
      if (assigned) {
        toast({ title: 'Supplier added', description: `"${created.supplier_name}" created and selected.` });
      } else {
        toast({ title: 'Supplier created but not yet selected', description: `"${created.supplier_name}" was created — pick it from the list to assign it.`, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Could not create supplier', description: getApiError(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!isEditable) {
    return <div className="font-medium text-sm">{reviewedSupplier?.supplier_name || supplierNameRaw || '\u2014'}</div>;
  }

  return (
    <div className="space-y-1" data-testid="ddi-supplier-resolver">
      {reviewedSupplier ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid="ddi-supplier-reviewed">
          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300">
            <CheckCircle2 className="h-3 w-3 mr-1" /> {reviewedSupplier.supplier_name}
          </Badge>
          <Popover open={pickerOpen} onOpenChange={openPicker}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost" className="h-6 text-xs" data-testid="ddi-supplier-change-btn">Change <ChevronDown className="h-3 w-3 ml-1" /></Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0">{renderPickerContent()}</PopoverContent>
          </Popover>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => save(null)} disabled={saving} data-testid="ddi-supplier-clear-btn">
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground truncate" title={supplierNameRaw}>Extracted: {supplierNameRaw || '\u2014'}</div>
          <div className="flex items-center gap-2 flex-wrap">
            {loadingSuggestion ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Checking supplier master…</span>
            ) : suggestion?.suggested_supplier ? (
              <>
                <span className="text-xs">Suggested:</span>
                <Badge
                  variant="outline"
                  className={suggestion.match_type === 'exact' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-amber-100 text-amber-800 border-amber-300'}
                  data-testid="ddi-supplier-suggestion-badge"
                >
                  {suggestion.suggested_supplier.supplier_name} ({Math.round(suggestion.confidence * 100)}%)
                </Badge>
                <Button size="sm" className="h-6 text-xs" onClick={() => save(suggestion.suggested_supplier.id)} disabled={saving} data-testid="ddi-supplier-confirm-suggestion-btn">
                  Confirm
                </Button>
              </>
            ) : (
              <span className="text-xs text-amber-700" data-testid="ddi-supplier-no-match">No confident match</span>
            )}
            <Popover open={pickerOpen} onOpenChange={openPicker}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" className="h-6 text-xs" data-testid="ddi-supplier-search-btn">
                  {suggestion?.suggested_supplier ? 'Not this' : 'Search / Add supplier'} <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0">{renderPickerContent()}</PopoverContent>
            </Popover>
          </div>
        </>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="ddi-add-supplier-dialog">
          <DialogHeader><DialogTitle>Add supplier</DialogTitle></DialogHeader>
          <Input
            value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Supplier name"
            data-testid="ddi-add-supplier-name-input"
          />
          {checkingName && <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Checking for existing suppliers…</div>}
          {similar.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-2 text-xs text-amber-800" data-testid="ddi-duplicate-name-warning">
              <div className="font-medium flex items-center gap-1 mb-1"><AlertTriangle className="h-3 w-3" /> Similar supplier(s) already exist</div>
              {similar.map(({ supplier, similarity }) => (
                <div key={supplier.id} className="flex items-center justify-between">
                  <span>{supplier.supplier_name} ({Math.round(similarity * 100)}% similar)</span>
                  <Button size="sm" variant="link" className="h-5 text-xs p-0" onClick={() => { setAddOpen(false); save(supplier.id); }} data-testid={`ddi-use-existing-supplier-${supplier.id}`}>
                    Use this instead
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="ddi-add-supplier-cancel">Cancel</Button>
            <Button
              onClick={createSupplier} disabled={!newName.trim() || saving}
              data-testid="ddi-add-supplier-save"
            >
              {similar.length > 0 ? 'Create anyway' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderPickerContent() {
    return (
      <Command>
        <CommandInput placeholder="Search suppliers…" data-testid="ddi-supplier-picker-input" />
        <CommandList>
          {!suppliers ? (
            <div className="p-3 text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
          ) : (
            <>
              <CommandEmpty>No supplier found.</CommandEmpty>
              <CommandGroup>
                {suppliers.map((s) => (
                  <CommandItem key={s.id} value={s.supplier_name} onSelect={() => save(s.id)} data-testid={`ddi-supplier-option-${s.id}`}>
                    {s.supplier_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
        <div className="border-t p-1">
          <Button
            variant="ghost" size="sm" className="w-full justify-start text-xs"
            onClick={() => { setPickerOpen(false); setNewName(supplierNameRaw || ''); setAddOpen(true); }}
            data-testid="ddi-supplier-open-add-dialog-btn"
          >
            <Plus className="h-3 w-3 mr-1" /> Add a new supplier
          </Button>
        </div>
      </Command>
    );
  }
}
